import { registryPresentation } from "../registryPresentation";
import { useTranslation } from "react-i18next";
import {
  IconBuilding,
  IconRosetteDiscountCheckFilled,
  IconUserCircle,
} from "@tabler/icons-react";
import identityPanelBackground from "../../../assets/images/onboarding/identity-panel.webp";
import { cn } from "../../../lib/utils";

/**
 * The desktop onboarding's right-hand half: the artwork, and a practitioner
 * badge that FILLS IN as the carousel is answered.
 *
 * It is the reason the desktop layout is worth having. On mobile the flow is a
 * column of questions and the answers disappear behind the next slide; here
 * every answer lands somewhere visible, so the RPPS lookup stops being a form
 * step and becomes the moment the badge gets a name, a specialty, a number and
 * a hospital on it.
 *
 * The badge is HTML, not part of the artwork: the artwork is a flat image
 * and only the ground behind it (Figma "D7.identite", "Doctor Badge Card"). Everything on the card
 * is real data or a placeholder for data that has not been given yet, which is
 * what makes the filling honest rather than a decoration that animates on a
 * timer.
 *
 * Purely presentational: no state, no effects, no refs.
 */

// Fixed bar widths, not random ones: a `Math.random` barcode reshuffles on every
// render of every step, which reads as the card flickering while you type.
const BARCODE_BARS = [
  "w-0.75",
  "w-px",
  "w-1",
  "w-0.5",
  "w-px",
  "w-0.75",
  "w-px",
  "w-1",
  "w-0.5",
  "w-px",
  "w-0.75",
] as const;

/** A line the practitioner has not reached yet, drawn as the design draws it. */
function PlaceholderBar({ className }: { className: string }) {
  return <span aria-hidden className={cn("block rounded-full", className)} />;
}

export function OnboardingIdentityPanel({
  firstName,
  lastName,
  specialtyLabel,
  registryId,
  registry,
  employer,
  foundRegistry,
}: {
  firstName: string;
  lastName: string;
  /** Null until the reference data resolves the declared specialty's label. */
  specialtyLabel: string | null;
  /** Empty string = no registry id confirmed or typed yet. */
  registryId: string;
  registry: keyof typeof registryPresentation | null;
  /** Empty string = the registry named no structure, or was never consulted. */
  employer: string;
  foundRegistry: keyof typeof registryPresentation | null;
}): React.JSX.Element {
  const { t } = useTranslation();

  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  const fullName = `${firstName} ${lastName}`.trim();

  return (
    // Below the desktop breakpoint the form column takes the whole width: the
    // artwork is the first thing worth dropping, not the questions.
    <aside className="hidden min-w-0 flex-1 p-8 lg:block">
      <div className="relative flex size-full items-center justify-center overflow-hidden rounded-4xl">
        <img
          src={identityPanelBackground}
          alt=""
          className="absolute inset-0 size-full object-cover"
        />

        {/* The 10px rim is a translucent white BORDER, not a ring or an
            outline: the design frames the badge with a frosted edge that lets
            the artwork through (`bg-clip-padding` keeps the white fill off it,
            `backdrop-blur-xs` frosts what shows). */}
        <div className="relative flex h-91 w-71 flex-col items-center overflow-hidden rounded-xl border-10 border-ds-white/32 bg-ds-white bg-clip-padding backdrop-blur-xs">
          {/* The lanyard slot. Decoration, and the only part that is. */}
          <div className="flex w-full justify-center p-2.5">
            <span aria-hidden className="h-2 w-12 rounded bg-ds-slate-200" />
          </div>

          {/* The verification, a chip in the card's corner. Earned by a
              confirmed record only. */}
          {foundRegistry !== null && (
            <div className="absolute top-10 right-3 flex items-center gap-1 rounded-sm bg-ds-blueberry-50 px-2 py-1">
              <IconRosetteDiscountCheckFilled className="size-4 text-ds-blueberry-600" />
              <span className="font-dm-sans text-ui-note font-medium text-ds-blueberry-600">
                {t("{{registry}} Verified", {
                  registry: registryPresentation[foundRegistry].sourceName,
                })}
              </span>
            </div>
          )}

          <div className="flex w-full flex-1 flex-col items-center gap-3 px-3 pt-18">
            <div className="flex size-13 items-center justify-center rounded-xl bg-ds-moss-200">
              {initials.length > 0 ? (
                <span className="font-dm-sans text-heading-md font-bold text-ds-moss-600">
                  {initials}
                </span>
              ) : (
                <IconUserCircle
                  aria-hidden
                  className="size-6 text-ds-moss-600"
                  stroke={1.5}
                />
              )}
            </div>

            <div className="flex w-full flex-col items-center gap-2">
              {fullName.length > 0 ? (
                <p className="m-0 text-center font-petrona text-desktop-badge-name font-medium text-ds-mint-800">
                  {fullName}
                </p>
              ) : (
                <PlaceholderBar className="h-6.5 w-51 bg-ds-mint-800/12" />
              )}

              {specialtyLabel === null ? (
                <PlaceholderBar className="h-3.5 w-27 bg-ds-slate-950/20" />
              ) : (
                <p className="m-0 text-center font-dm-sans text-xs text-ds-slate-950/60">
                  {specialtyLabel}
                </p>
              )}

              {registryId.length === 0 ? (
                registry === "reps" ? (
                  <p className="m-0 text-center font-dm-sans text-xs text-ds-slate-950/60">
                    {t("REPS public record")}
                  </p>
                ) : (
                  <PlaceholderBar className="h-2.25 w-16 bg-ds-slate-700/20" />
                )
              ) : (
                <p className="m-0 text-center font-dm-sans text-xs text-ds-slate-950/60">
                  {registryId}
                </p>
              )}
            </div>

            <div className="flex w-full items-center justify-center gap-2">
              <IconBuilding
                aria-hidden
                className="size-4 shrink-0 text-ds-slate-700"
                stroke={1.15}
              />
              {employer.length === 0 ? (
                <PlaceholderBar className="h-3.5 w-42 bg-ds-slate-950/20" />
              ) : (
                <p className="m-0 min-w-0 truncate font-dm-sans text-desktop-badge-meta text-ds-black">
                  {employer}
                </p>
              )}
            </div>
          </div>

          <div
            aria-hidden
            className="flex w-full flex-col items-center gap-1 py-1"
          >
            <div className="flex h-6 items-end gap-0.5">
              {BARCODE_BARS.map((width, index) => (
                <span
                  key={index}
                  className={cn("h-full bg-ds-slate-100", width)}
                />
              ))}
            </div>
            <span className="font-dm-sans text-ui-caption text-ds-slate-200">
              {`ID-JUISCI${initials.length > 0 ? `-${initials}` : ""}`}
            </span>
          </div>
        </div>
      </div>
    </aside>
  );
}
